import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';

// PrimeNG Modules
import { FileUploadModule, FileUploadHandlerEvent } from 'primeng/fileupload';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { MessagesModule } from 'primeng/messages';
import { MessageService } from 'primeng/api';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ToastModule } from 'primeng/toast';
import { TableModule } from 'primeng/table';

// Services and Models
import { SelloutService, SelloutUploadResponse } from '../sellout.service';
import { ChatSession } from '../../models/chat-session.model';
import { ChatSessionService } from '../../services/chat-session.service';
import { FirebaseStorageService } from '../../services/firebase-storage.service';

@Component({
  selector: 'app-sellout-upload',
  standalone: true,
  imports: [
    CommonModule,
    FileUploadModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    MessagesModule,
    ProgressSpinnerModule,
    ToastModule
  ],
  providers: [MessageService],
  templateUrl: './sellout-upload.component.html',
  styleUrls: ['./sellout-upload.component.css']
})
export class SelloutUploadComponent implements OnInit, OnDestroy {

  templateColumns: { name: string; mandatory: string; validation: string }[] = [
    { name: 'Customer number', mandatory: 'Yes', validation: 'Must match the mapped customer of the selected customer item' },
    { name: 'Customer Item No', mandatory: 'Yes', validation: 'Must exist in Customer Item Mapping' },
    { name: 'Compensation per unit', mandatory: 'Yes', validation: 'Numeric' },
    { name: 'Quantity', mandatory: 'Yes', validation: 'Whole number >= 0' },
    { name: 'Total Amount', mandatory: 'Yes', validation: 'Number > 0' },
    { name: 'Start date', mandatory: 'Yes', validation: 'Date (YYYY-MM-DD)' },
    { name: 'End date', mandatory: 'Yes', validation: 'Date (YYYY-MM-DD)' },
    { name: 'PP ID', mandatory: 'No', validation: 'Optional numeric promo plan ID; if provided, matching is restricted to that promo plan' },
    { name: 'batch number', mandatory: 'No', validation: 'Optional promo plan batch scope; if provided, matching is restricted to that batch' }
  ];

  fileToUpload: File | null = null;
  isLoading: boolean = false;
  isProcessing: boolean = false;
  isUploadComplete: boolean = false;

  private routeSub!: Subscription;
  private sessionSub!: Subscription;
  currentSessionId: number | null = null;
  settlementId: number | null = null;
  currentSession: ChatSession | undefined;


  constructor(
    private messageService: MessageService,
    private selloutService: SelloutService,
    private route: ActivatedRoute,
    private router: Router,
    private chatSessionService: ChatSessionService,
    private firebaseStorageService: FirebaseStorageService
  ) { }

  ngOnInit(): void {
    const settlementIdParam = this.route.snapshot.paramMap.get('settlementId');
    this.settlementId = settlementIdParam ? Number(settlementIdParam) : null;
    if (!this.settlementId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Routing Error',
        detail: 'Sellout upload must start from a settlement.'
      });
    }

    // The traversal logic is confirmed to be working. We can remove the verbose logging.
    let routeWithSessionId: ActivatedRoute | null = this.route;
    while (routeWithSessionId && !routeWithSessionId.snapshot.paramMap.has('sessionId')) {
      routeWithSessionId = routeWithSessionId.parent;
    }

    this.routeSub = (routeWithSessionId || this.route).paramMap.subscribe(params => {
      const sessionIdParam = params.get('sessionId');
      if (sessionIdParam) {
        this.currentSessionId = +sessionIdParam;
        this.sessionSub = this.chatSessionService.sessions$.subscribe(sessions => {
          this.currentSession = sessions.find(s => s.sessionId === this.currentSessionId);
        });
      } else {
        const errorMsg = 'Session ID not found in route parameters.';
        this.messageService.add({ severity: 'error', summary: 'Routing Error', detail: errorMsg });
        console.error(`[SelloutUploadComponent] ERROR: ${errorMsg}`);
      }
    });
  }

  async onFileSelect(event: FileUploadHandlerEvent | any): Promise<void> {
    this.isLoading = true;
    this.fileToUpload = null;
    this.isUploadComplete = false;

    const file = event.files[0];
    this.fileToUpload = file;
    this.isLoading = false;
    this.messageService.add({ severity: 'success', summary: 'File Selected', detail: `Ready to upload ${file.name}.` });
  }

  customUploadHandler(event: FileUploadHandlerEvent): void {
    this.onFileSelect(event);
  }

  sendSelloutToBackend(): void {
    if (!this.fileToUpload) {
      this.messageService.add({ severity: 'warn', summary: 'No File', detail: 'Please select an Excel file first.' });
      return;
    }
    if (!this.currentSessionId) {
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Session ID is missing. Cannot send data.' });
      return;
    }
    if (!this.settlementId) {
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Settlement ID is missing. Sellout upload must come from a settlement.' });
      return;
    }
    const settlementId = this.settlementId;

    this.isProcessing = true;
    const filePath = `sellouts/${this.currentSessionId}/${this.fileToUpload.name}`;

    this.firebaseStorageService.uploadFile(this.fileToUpload, filePath).subscribe({
      next: (downloadURL) => {
        this.messageService.add({ severity: 'info', summary: 'Upload Started', detail: 'File uploaded to cloud storage. Sending to backend for processing...' });
        console.log('File uploaded successfully to Firebase Storage:', downloadURL);
        this.selloutService.uploadSellout(this.currentSessionId!, downloadURL, settlementId).subscribe({
          next: (response: SelloutUploadResponse) => {
            this.isProcessing = false;
            this.isUploadComplete = true;
            this.messageService.add({ severity: 'success', summary: 'Upload Successful', detail: response.message || 'Sellout created successfully.' });
            console.log('Sellout file uploaded successfully', response);
            this.fileToUpload = null;
            this.router.navigate(
              ['/ai-studio/session', this.currentSessionId!, { outlets: { canvasOutlet: ['sellout', 'view', response.selloutId] } }]
            );
          },
          error: (err) => {
            this.isProcessing = false;
            const backendError = err?.error;
            const errorDetail = typeof backendError === 'string' && backendError.trim().length > 0
              ? backendError
              : typeof backendError?.message === 'string' && backendError.message.trim().length > 0
                ? backendError.message
                : typeof err?.message === 'string' && err.message.trim().length > 0
                  ? err.message
                  : 'Could not send sellout file to backend.';
            this.messageService.add({ severity: 'error', summary: 'Upload Failed', detail: errorDetail, life: 12000 });
            console.error('Error uploading sellout file', err);
          }
        });
      },
      error: (err) => {
        this.isProcessing = false;
        this.messageService.add({ severity: 'error', summary: 'Upload Failed', detail: 'Could not upload file to cloud storage.' });
        console.error('Error uploading file to storage', err);
      }
    });
  }

  ngOnDestroy(): void {
    this.routeSub?.unsubscribe();
    this.sessionSub?.unsubscribe();
  }
}
